import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, MapPin, Truck, Package, Calendar, FileText, ImageOff, X } from "lucide-react";
import axios from "axios";
import { BrandLockup } from "../components/Brand";
import dict from "../i18n/es-MX";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Plain axios with no auth interceptor for the public flow.
const publicApi = axios.create({ baseURL: `${BASE}/api/public`, timeout: 15000 });

import { fmtDate, fmtDateTime } from "../lib/dateFormat";

// Single image with built-in lazy load + error state. Public photos don't need
// auth headers so we can use <img src=…> directly.
function GalleryThumb({ photo, onClick }) {
    const [errored, setErrored] = useState(false);
    const src = `${BASE}${photo.thumb_url}`;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`gallery-thumb-${photo.id}`}
            className="group relative aspect-square overflow-hidden rounded-lg border border-slate-800 hover:border-brand-orange transition focus:outline-none focus:ring-2 focus:ring-brand-orange/60"
        >
            {!errored ? (
                <img
                    src={src} alt=""
                    loading="lazy"
                    onError={() => setErrored(true)}
                    className="w-full h-full object-cover group-hover:scale-105 transition"
                />
            ) : (
                <div className="w-full h-full bg-slate-800 text-slate-500 flex items-center justify-center">
                    <ImageOff size={18}/>
                </div>
            )}
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/90 to-transparent text-[10px] text-slate-100 px-1.5 py-1 text-left truncate">
                {photo.asset_nickname} • {photo.checklist_label_es}
            </div>
        </button>
    );
}

function Lightbox({ photo, onClose }) {
    useEffect(() => {
        if (!photo) return;
        const k = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", k);
        return () => window.removeEventListener("keydown", k);
    }, [photo, onClose]);
    if (!photo) return null;
    return (
        <div
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="track-lightbox"
        >
            <div className="max-w-5xl w-full max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <img
                    src={`${BASE}${photo.thumb_url}`}
                    alt=""
                    className="max-h-[85vh] w-auto object-contain rounded-lg shadow-2xl"
                />
                <div className="text-xs text-slate-300 text-center" data-testid="track-lightbox-caption">
                    {photo.asset_nickname} • {photo.moment_label_es} • {photo.checklist_label_es} • {fmtDateTime(photo.taken_at)}
                </div>
                <button type="button" onClick={onClose} data-testid="track-lightbox-close"
                    className="px-4 h-9 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm border border-slate-700 inline-flex items-center gap-1.5">
                    <X size={14}/> {dict.common.close}
                </button>
            </div>
        </div>
    );
}

function StatusPill({ status, label }) {
    const map = {
        programado: "bg-sky-500/15 text-sky-300 border-sky-500/40",
        en_progreso: "bg-amber-500/15 text-amber-300 border-amber-500/40",
        completado: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
        cancelado: "bg-red-500/15 text-red-300 border-red-500/40",
    };
    return (
        <span
            data-testid={`track-status-${status}`}
            className={`inline-flex items-center px-3 h-7 rounded-full text-xs font-semibold uppercase tracking-wider border ${map[status] || "bg-slate-800 text-slate-200 border-slate-700"}`}
        >
            {label}
        </span>
    );
}

function NotAvailable() {
    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
            <div className="h-1" style={{ background: "repeating-linear-gradient(135deg, #F97316 0 18px, #0f172a 18px 36px)" }} />
            <header className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <BrandLockup size={36}/>
                <span className="text-xs uppercase tracking-wider text-slate-500">Seguimiento</span>
            </header>
            <main className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md" data-testid="track-not-available">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-800 flex items-center justify-center mb-4">
                        <ImageOff size={26} className="text-slate-500"/>
                    </div>
                    <h1 className="font-display text-2xl font-bold text-slate-100 mb-2">{dict.track.notAvailable}</h1>
                    <p className="text-sm text-slate-400">{dict.track.notAvailableHint}</p>
                </div>
            </main>
            <footer className="px-6 py-3 border-t border-slate-800 text-center text-[11px] text-slate-500">
                {dict.track.footerBy}
            </footer>
        </div>
    );
}

export default function PublicTrack() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [state, setState] = useState("loading"); // loading | ok | not_found
    const [lightbox, setLightbox] = useState(null);
    const [tab, setTab] = useState("despues");

    useEffect(() => {
        document.title = "Seguimiento del trabajo • Flotilla MX";
    }, []);

    useEffect(() => {
        let alive = true;
        publicApi.get(`/jobs/${token}`)
            .then((r) => { if (alive) { setData(r.data); setState("ok"); } })
            .catch(() => { if (alive) setState("not_found"); });
        return () => { alive = false; };
    }, [token]);

    // Determine default tab from available data.
    useEffect(() => {
        if (!data) return;
        const anyDespues = data.photos?.some((p) => p.moment === "despues");
        const anyAntes = data.photos?.some((p) => p.moment === "antes");
        if (anyDespues) setTab("despues");
        else if (anyAntes) setTab("antes");
    }, [data]);

    // Group photos by asset for the current tab.
    const grouped = useMemo(() => {
        if (!data) return {};
        const map = {};
        for (const p of data.photos || []) {
            if (p.moment !== tab) continue;
            (map[p.asset_nickname] = map[p.asset_nickname] || []).push(p);
        }
        return map;
    }, [data, tab]);

    if (state === "loading") {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center" data-testid="track-loading">
                <Loader2 className="animate-spin text-slate-500" size={28}/>
            </div>
        );
    }
    if (state === "not_found") return <NotAvailable/>;
    if (!data) return null;

    const hasAntes = (data.photos || []).some((p) => p.moment === "antes");
    const hasDespues = (data.photos || []).some((p) => p.moment === "despues");

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col" data-testid="track-page">
            <div className="h-1" style={{ background: "repeating-linear-gradient(135deg, #F97316 0 18px, #0f172a 18px 36px)" }} />
            <header className="px-4 sm:px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 z-10 bg-slate-950/85 backdrop-blur">
                <BrandLockup size={36}/>
                <span className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Seguimiento del trabajo</span>
            </header>

            <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* Hero card */}
                <section className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-5 sm:p-6 space-y-3" data-testid="track-hero">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500">Trabajo</div>
                            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50 mt-0.5" data-testid="track-code">
                                {data.code}
                            </h1>
                        </div>
                        <StatusPill status={data.status} label={data.status_label_es}/>
                    </div>

                    {data.status === "cancelado" && (
                        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200" data-testid="track-cancelled-banner">
                            {dict.track.cancelledBanner}
                        </div>
                    )}

                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm pt-2">
                        <div>
                            <dt className="text-[10px] uppercase tracking-wider text-slate-500">{dict.track.client}</dt>
                            <dd className="text-slate-100 font-medium" data-testid="track-client">{data.client_name}</dd>
                        </div>
                        <div>
                            <dt className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><MapPin size={11}/>{dict.track.location}</dt>
                            <dd className="text-slate-200" data-testid="track-location">{data.location_text || "—"}</dd>
                        </div>
                        <div>
                            <dt className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><Calendar size={11}/>{dict.track.period}</dt>
                            <dd className="text-slate-200" data-testid="track-period">{fmtDate(data.start_date)}{data.end_date ? ` — ${fmtDate(data.end_date)}` : ""}</dd>
                        </div>
                        {data.viajes_count > 0 && (
                            <div>
                                <dt className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><Truck size={11}/>{dict.track.viajesTitle}</dt>
                                <dd className="text-brand-orange font-display text-xl font-bold" data-testid="track-viajes-count">{data.viajes_count}</dd>
                            </div>
                        )}
                    </dl>

                    {data.description && (
                        <div className="pt-2 border-t border-slate-800">
                            <dt className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1 mb-1"><FileText size={11}/>{dict.track.description}</dt>
                            <dd className="text-sm text-slate-300 whitespace-pre-line" data-testid="track-description">{data.description}</dd>
                        </div>
                    )}
                </section>

                {/* Assets */}
                <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5" data-testid="track-assets">
                    <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3 inline-flex items-center gap-1"><Truck size={12}/>{dict.track.assetsTitle}</h2>
                    {(data.asset_summaries || []).length === 0 ? (
                        <p className="text-sm text-slate-500">—</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {data.asset_summaries.map((a, i) => (
                                <div key={i} className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-slate-800/80 border border-slate-700" data-testid={`track-asset-${i}`}>
                                    <span className="font-semibold text-slate-100">{a.nickname}</span>
                                    <span className="text-[11px] text-slate-400">{a.type_label_es}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Materials */}
                {(data.material_summaries || []).length > 0 && (
                    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5" data-testid="track-materials">
                        <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3 inline-flex items-center gap-1"><Package size={12}/>{dict.track.materialsTitle}</h2>
                        <table className="min-w-full text-sm">
                            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left py-2">{dict.track.materialsHeaderName}</th>
                                    <th className="text-right py-2">{dict.track.materialsHeaderQty}</th>
                                    <th className="text-left py-2 pl-3">{dict.track.materialsHeaderUnit}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.material_summaries.map((m, i) => (
                                    <tr key={i} className="border-t border-slate-800" data-testid={`track-material-${i}`}>
                                        <td className="py-2 text-slate-100">{m.name}</td>
                                        <td className="py-2 text-right font-mono text-slate-200">{m.quantity ?? "—"}</td>
                                        <td className="py-2 pl-3 text-slate-400">{m.unit_label_es}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Photos */}
                <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5" data-testid="track-photos">
                    <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">{dict.track.photosTitle}</h2>
                    {(data.photos || []).length === 0 ? (
                        <p className="text-sm text-slate-500">{dict.track.noPhotos}</p>
                    ) : (
                        <>
                            <div className="flex gap-1 mb-4 border-b border-slate-800">
                                {hasAntes && (
                                    <button type="button" onClick={() => setTab("antes")}
                                        data-testid="track-tab-antes"
                                        className={`-mb-px px-4 h-9 text-xs font-semibold uppercase tracking-wider border-b-2 transition ${tab === "antes" ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                                        {dict.track.antes}
                                    </button>
                                )}
                                {hasDespues && (
                                    <button type="button" onClick={() => setTab("despues")}
                                        data-testid="track-tab-despues"
                                        className={`-mb-px px-4 h-9 text-xs font-semibold uppercase tracking-wider border-b-2 transition ${tab === "despues" ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                                        {dict.track.despues}
                                    </button>
                                )}
                            </div>

                            {Object.keys(grouped).length === 0 ? (
                                <p className="text-sm text-slate-500">{dict.track.noPhotos}</p>
                            ) : (
                                <div className="space-y-5">
                                    {Object.entries(grouped).map(([nick, items]) => (
                                        <div key={nick}>
                                            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{nick}</div>
                                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                                {items.map((p) => (
                                                    <GalleryThumb key={p.id} photo={p} onClick={() => setLightbox(p)}/>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </section>
            </main>

            <footer className="px-6 py-4 border-t border-slate-800 text-center text-[11px] text-slate-500" data-testid="track-footer">
                {dict.track.footerBy} • {dict.track.footerGenerated} {fmtDateTime(data.generated_at)}
            </footer>

            <Lightbox photo={lightbox} onClose={() => setLightbox(null)}/>
        </div>
    );
}
